"use client";

import { cn } from "./utils";
import { motion } from "motion/react";

export interface FundraisingProgressTrackerProps {
  current: number;
  goal: number;
  pending?: number;
  formatCurrency?: (value: number) => string;
  className?: string;
}

/**
 * A progress tracker component for showing fundraising progress against goals.
 * Shows committed capital, pending pipeline, and subscription status.
 */
export function FundraisingProgressTracker({
  current,
  goal,
  pending = 0,
  formatCurrency = defaultFormatCurrency,
  className,
}: FundraisingProgressTrackerProps) {
  const progressPct = goal > 0 ? (current / goal) * 100 : 0;
  const pipelinePct = goal > 0 ? ((current + pending) / goal) * 100 : 0;
  const isOversubscribed = progressPct >= 100;
  const remaining = Math.max(0, goal - current);

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {/* Header with big numbers */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tracking-tight text-neutral-900">
              {formatCurrency(current)}
            </span>
            <span className="text-sm font-medium text-neutral-500">
              raised of {formatCurrency(goal)} goal
            </span>
          </div>
          {pending > 0 && (
            <div className="mt-1 text-sm text-neutral-500">
              <span className="font-medium text-emerald-600">
                +{formatCurrency(pending)}
              </span>{" "}
              in pending pipeline
            </div>
          )}
        </div>

        <div className="text-right">
          <div
            className={cn(
              "text-2xl font-bold",
              isOversubscribed ? "text-emerald-600" : "text-neutral-900"
            )}
          >
            {progressPct.toFixed(0)}%
          </div>
          <div className="text-xs font-medium uppercase tracking-wider text-neutral-500">
            Progress
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative h-3 w-full overflow-hidden rounded-full bg-neutral-100">
        {/* Pending (lighter) */}
        {pending > 0 && (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(pipelinePct, 100)}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="absolute left-0 top-0 h-full bg-emerald-200"
          />
        )}
        {/* Committed (darker) */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(progressPct, 100)}%` }}
          transition={{ duration: 1, ease: "easeOut" }}
          className={cn(
            "absolute left-0 top-0 h-full",
            isOversubscribed ? "bg-emerald-500" : "bg-neutral-900"
          )}
        />
      </div>

      {/* Footer/Legend */}
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "h-2 w-2 rounded-full",
                isOversubscribed ? "bg-emerald-500" : "bg-neutral-900"
              )}
            />
            <span>Committed</span>
          </div>
          {pending > 0 && (
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-emerald-200" />
              <span>Pending</span>
            </div>
          )}
        </div>
        <div>
          {remaining > 0
            ? `${formatCurrency(remaining)} remaining`
            : "Goal met"}
        </div>
      </div>
    </div>
  );
}

function defaultFormatCurrency(value: number): string {
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
